import useDelivery from "../components/composables/delivery-estimator";

// ==================== CONSTANTS ====================

const CONFIG = {
  // eslint-disable-next-line no-undef
  BASE_URL: SDG.Data.joineryHost || "https://staging.framebridge.com/",
  ESTIMATED_DELIVERY_MESSAGING_ROUTE: "api/v1/estimated_delivery_messaging",
  DEFAULT_CONVEYANCE: "digital",
  ZIPCODE_LENGTH: 5,
  ZIPCODE_REGEX: /^\d{5}$/,
  DATE_FORMAT: { month: "short", day: "numeric", timeZone: "UTC" },
  TYPOGRAPHY: {
    FIGMA_14PX: {
      fontFamily: "Apercu Pro, sans-serif",
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "24px",
      letterSpacing: "0.28px",
      color: "#1D2019"
    },
    FIGMA_12PX: {
      fontFamily: "Apercu Pro, sans-serif",
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "18px",
      letterSpacing: "0.24px",
      color: "#1D2019"
    }
  }
};

const ELEMENT_IDS = {
  COMPONENT: "enhancedDeliveryEstimate",
  WITH_ZIP: "deliveryWithZip",
  CUSTOM_FRAME: "deliveryCustomFrame",
  CUSTOM_FRAME_DETAILS: "deliveryCustomFrameDetails",
  NO_ZIP: "deliveryNoZip",
  ZIP_INPUT: "deliveryZipInput",
  ICON_WITH_ZIP: "deliveryIconWithZip",
  ICON_CUSTOM_FRAME: "deliveryIconCustomFrame",
  ICON_CUSTOM_FRAME_DETAILS: "deliveryIconCustomFrameDetails",
  CUSTOM_FRAME_MAIN: "customFrameMainContent",
  CUSTOM_FRAME_INFO_BTN: "customFrameInfoButton",
  ZIPCODE_BUTTON: "zipcodeButton",
  ZIPCODE_INPUT: "zipcodeInput",
  SUBMIT_ZIPCODE: "submitZipcode",
  ZIPCODE_ERROR: "zipcodeError",
  ENTER_ZIPCODE_BUTTON: "enterZipcodeButton",
  CLOSE_CUSTOM_FRAME_DETAILS: "closeCustomFrameDetails"
};

const CSS_CLASSES = {
  HIDE: "hide",
  TITLE: "delivery-estimate__title",
  SHIPPING: "delivery-estimate__shipping",
  FREE_SHIPPING: "delivery-estimate__free-shipping",
  MAIN: "delivery-estimate__main",
  CONTENT: "delivery-estimate__content",
  ICON: "delivery-estimate__icon",
  ZIPCODE_BTN: "delivery-estimate__zipcode-btn"
};

const SCENARIOS = {
  WITH_ZIP: "withZip",
  CUSTOM_FRAME: "customFrame",
  CUSTOM_FRAME_DETAILS: "customFrameDetails",
  NO_ZIP: "noZip",
  ZIP_INPUT: "zipInput"
};

const DIGITAL_ART_TYPE_SLUGS = ["digital_upload", "digital_photo"];

// ==================== MAIN COMPONENT ====================

/**
 * Enhanced Delivery Estimate Component
 * Handles 5 UX scenarios based on product type and zip code state
 * Integrates with dynamic delivery messaging API
 */
function enhancedDeliveryEstimate() {  
  // Check if component exists
  const component = document.getElementById(ELEMENT_IDS.COMPONENT);
  
  if (!component) {
    console.error("❌ Enhanced Delivery Estimate: Component not found in DOM");
    return;
  }

  // Prevent multiple initializations
  if (component.dataset.initialized === "true") {
    return;
  }
  
  component.dataset.initialized = "true";

  // Get product data from component attributes
  const productType = component.dataset.productType;
  const hasTabletopFrames = component.dataset.hasTabletopFrames === "true";
  const hasQuickShip = component.dataset.hasQuickShip === "true";
  const isPersonalizedTabletop = component.dataset.isPersonalizedTabletop === "true";
  const inStoreFulfillable = component.dataset.inStoreFulfillable === "true";
  const productionCategory = component.dataset.productionCategory || "";
  
  let conveyance = component.dataset.conveyance || CONFIG.DEFAULT_CONVEYANCE;
  
  if (productType === "custom frame") {
    conveyance = "digital"; // Default to digital for custom frames
  }
  
  // Store the current SLA type (will be determined from matched messaging config)
  let currentSlaType = null;

  // Cache for API data
  let deliveryMessagingCache = null;
  let cachedMatchedMessaging = null;
  let detailsRendered = false;

  // Conveyance picked in the "What Do You Want To Frame?" dropdown, and the one whose content is rendered
  let selectedConveyance = null;
  let renderedConveyance = null;

  // Parse store availability data
  let storeAvailabilities = [];
  try {
    const availabilityData = component.dataset.storeAvailabilities;
    if (availabilityData && availabilityData.trim() !== "") {
      storeAvailabilities = JSON.parse(availabilityData);
    } else {
      console.warn("Enhanced Delivery Estimate: No store availability data in component", {
        hasData: Boolean(availabilityData),
        dataLength: availabilityData?.length
      });
    }
  } catch (error) {
    console.warn("Error parsing store availabilities:", error);
  }

  // ==================== DOM REFERENCES ====================

  const scenarios = {
    [SCENARIOS.WITH_ZIP]: document.getElementById(ELEMENT_IDS.WITH_ZIP),
    [SCENARIOS.CUSTOM_FRAME]: document.getElementById(ELEMENT_IDS.CUSTOM_FRAME),
    [SCENARIOS.CUSTOM_FRAME_DETAILS]: document.getElementById(ELEMENT_IDS.CUSTOM_FRAME_DETAILS),
    [SCENARIOS.NO_ZIP]: document.getElementById(ELEMENT_IDS.NO_ZIP),
    [SCENARIOS.ZIP_INPUT]: document.getElementById(ELEMENT_IDS.ZIP_INPUT)
  };

  // DOM elements for layout control
  const pickupSection = component.querySelector(".enhanced-delivery-estimate__pickup");
  const personalizedMessageSection = component.querySelector(".enhanced-delivery-estimate__personalized-message");
  const containerSection = component.querySelector(".enhanced-delivery-estimate__container");
  const findStoreBtn = component.querySelector(".js-find-store-btn");

  // Pickup section elements
  const pickupTitle = component.querySelector(".pickup__title");
  const pickupTime = component.querySelector(".pickup__time");

  const elements = {
    deliveryDate: document.getElementById("deliveryDate"),
    currentZipcode: document.getElementById("currentZipcode"),
    zipcodeButton: document.getElementById(ELEMENT_IDS.ZIPCODE_BUTTON),
    enterZipcodeButton: document.getElementById(ELEMENT_IDS.ENTER_ZIPCODE_BUTTON),
    zipcodeInput: document.getElementById(ELEMENT_IDS.ZIPCODE_INPUT),
    submitZipcode: document.getElementById(ELEMENT_IDS.SUBMIT_ZIPCODE),
    zipcodeError: document.getElementById(ELEMENT_IDS.ZIPCODE_ERROR),
    expeditedDate: document.getElementById("expeditedDate"),
    tooltip: document.getElementById("deliveryTooltip"),
    modal: document.getElementById("zipcodeModal"),
    closeModal: document.getElementById("closeModal"),
    customFrameDeliveryDate: document.getElementById("customFrameDeliveryDate"),
    customFrameCurrentZipcode: document.getElementById("customFrameCurrentZipcode"),
    customFrameZipcodeButton: document.getElementById("customFrameZipcodeButton"),
    customFrameInfoButton: document.getElementById(ELEMENT_IDS.CUSTOM_FRAME_INFO_BTN),
    closeCustomFrameDetails: document.getElementById(ELEMENT_IDS.CLOSE_CUSTOM_FRAME_DETAILS),
    detailsDigitalDate: document.getElementById("detailsDigitalDate"),
    detailsDigitalExpedited: document.getElementById("detailsDigitalExpedited"),
    detailsPhysicalDate: document.getElementById("detailsPhysicalDate"),
    detailsMailInDate: document.getElementById("detailsMailInDate")
  };

  let deliveryData = null;

  // ==================== STORE PICKUP FUNCTIONS ====================

  /**
   * Get selected store from localStorage (default) or sessionStorage
   */
  function getSelectedStore() {
    try {
      const defaultStore = localStorage.getItem("selectedStore");
      if (defaultStore) {
        const store = JSON.parse(defaultStore);
        if (store && (store.name || store.id)) return store;
      }
      const nearestStoresData = sessionStorage.getItem("nearestStoresData");
      if (nearestStoresData) {
        const stores = JSON.parse(nearestStoresData);
        return stores && stores.length > 0 ? stores[0] : null;
      }
    } catch (error) {
      console.error("Error getting selected store:", error);
    }
    return null;
  }

  /**
   * Decode HTML entities in a string
   */
  function decodeHtmlEntities(text) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Get store availability info for a specific store
   */
  function getStoreAvailability(store) {
    if (!store || !storeAvailabilities || storeAvailabilities.length === 0) {
      console.warn("Enhanced Delivery Estimate: No store availability data", {
        hasStore: Boolean(store),
        availabilitiesCount: storeAvailabilities.length,
        storeName: store?.name
      });
      return null;
    }

    // Decode HTML entities from store name for proper matching
    const decodedStoreName = decodeHtmlEntities(store.name);

    // Try to match by location name (comparing decoded names)
    const availability = storeAvailabilities.find(avail => {
      const decodedLocationName = decodeHtmlEntities(avail.location_name);
      return decodedLocationName === decodedStoreName;
    });

    if (!availability) {
      console.warn("Enhanced Delivery Estimate: No availability match found", {
        storeName: store.name,
        decodedStoreName: decodedStoreName,
        availableLocations: storeAvailabilities.map(avail => ({
          raw: avail.location_name,
          decoded: decodeHtmlEntities(avail.location_name)
        }))
      });
    }

    return availability;
  }

  /**
   * Fetch store pickup availability from API
   */
  async function fetchStorePickupAvailability(joineryStoreId) {
    if (!joineryStoreId) {
      console.warn("Enhanced Delivery Estimate: No joinery store ID provided");
      return null;
    }

    try {
      const response = await fetch(`${CONFIG.BASE_URL}api/v1/store_pickup_availability?store_id=${joineryStoreId}`);
      
      if (!response.ok) {
        return { error: true };
      }

      const data = await response.json();
      if (data) {
        return data;
      }

      // Return error object to distinguish from no data
      return { error: true };
    } catch (error) {
      console.error("Error fetching store pickup availability:", error);
      // Return error object to distinguish from no data
      return { error: true };
    }
  }

  /**
   * Format time to cutoff in human-readable format
   */
  function formatTimeToCutoff(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);

    if (hours > 0 && mins > 0) {
      return `${hours} hrs ${mins} mins`;
    } else if (hours > 0) {
      return `${hours} hrs`;
    } else {
      return `${mins} mins`;
    }
  }

  /**
   * Calculate pickup time text from API response
   */
  function calculatePickupTimeText(pickupData) {
    if (!pickupData) {
      console.warn("Enhanced Delivery Estimate: No pickup data provided");
      return { text: "Usually ready in 2-3 hours", availableToday: true };
    }

    const { availableToday, timeToCutoff, nextAvailableDate, cutoffTimeDisplay } = pickupData;

    // If all values are null, the store likely doesn't have google_place_id configured
    if (availableToday === null && timeToCutoff === null && nextAvailableDate === null) {
      if (pickupData.hasGooglePlaceId === false) {
        console.warn(`Enhanced Delivery Estimate: Store ID ${pickupData.storeId} is missing google_place_id. Please configure it in the Spree::Store model.`);
      } else {
        console.warn("Enhanced Delivery Estimate: Store not configured for pickup availability (missing store hours from Google Places)");
      }
      return { text: "Usually ready in 2-3 hours", availableToday: false, hoursUnavailable: true };
    }

    // Not available today - show next available date
    if (!availableToday) {
      if (nextAvailableDate) {
        const nextDate = new Date(nextAvailableDate);
        const dateText = nextDate.toLocaleDateString("en-US", { 
          month: "short", 
          day: "numeric",
          timeZone: "UTC"
        });
        return { text: `Ready for pickup on ${dateText}`, availableToday: false };
      }
      return { text: "Usually ready in 2-3 hours", availableToday: true };
    }

    // Available today - show time window with cutoff
    if (availableToday && cutoffTimeDisplay && timeToCutoff !== undefined && timeToCutoff !== null) {
      const timeText = formatTimeToCutoff(timeToCutoff);
      return { 
        text: `Order within <span class="pickup__time-countdown">${timeText}</span> get it today`, 
        availableToday: true 
      };
    }

    return { text: "Usually ready in 2-3 hours", availableToday: true };
  }

  /**
   * Check if pickup is available today based on pickup time string
   * Examples: "Usually ready in 2-4 hours", "Usually ready in 24 hours", "Ready in 1-2 days"
   */
  function isPickupAvailableToday(pickupTimeString) {
    if (!pickupTimeString) {
      return true; // Default to today if no time specified
    }

    const lowerTime = pickupTimeString.toLowerCase();
    
    // Check for "days" or "day" in the string - if present, not available today
    if (lowerTime.includes("day") && !lowerTime.includes("today")) {
      return false;
    }

    // Check for "hours" - if it mentions hours, it's likely same day
    if (lowerTime.includes("hour")) {
      // Extract the number of hours if possible
      const hoursMatch = lowerTime.match(/(\d+)\s*(?:-\s*(\d+))?\s*hour/);
      if (hoursMatch) {
        const maxHours = hoursMatch[2] ? parseInt(hoursMatch[2], 10) : parseInt(hoursMatch[1], 10);
        // If more than 24 hours, not today
        return maxHours <= 24;
      }
      return true; // Default to today for hour-based times
    }

    // If it explicitly says "today", it's available today
    if (lowerTime.includes("today")) {
      return true;
    }

    // Default to today if we can't determine
    return true;
  }

  /**
   * Open the store selector modal
   */
  function openStoreSelector() {
    const $body = document.body;
    $body.classList.add("is-store-selector-exposed");

    // Populate zip code in store selector if available
    const { zipcode } = useDelivery();
    const storeZipInput = document.getElementById("zip_code");
    if (storeZipInput && zipcode.value) {
      storeZipInput.value = zipcode.value;

      // Add has-value class to parent to trigger floating label
      const inputWrapper = storeZipInput.closest(".input-placeholder");
      if (inputWrapper) {
        inputWrapper.classList.add("has-value");
      }

      // Auto-submit the search to show distances
      setTimeout(() => {
        const searchButton = document.querySelector(".js-store-selector-search-btn button");
        if (searchButton) {
          searchButton.click();
        }
      }, 300); // Small delay to ensure modal is fully open
    }
  }

  /**
   * Reset pickup section to default state
   */
  function resetPickupSection() {
    if (pickupTitle) {
      pickupTitle.textContent = "Available for Store Pickup";
    }
    if (pickupTime) {
      pickupTime.textContent = "Usually ready in 2-3 hours";
    }
    if (findStoreBtn) {
      findStoreBtn.textContent = "Find A Store";
    }
  }

  /**
   * Get pickup time from API or fallback to configured time
   */
  async function getPickupTime(availability) {
    if (!availability.joinery_store_id) {
      return null;
    }

    const pickupData = await fetchStorePickupAvailability(availability.joinery_store_id);
    
    // If API returned an error, return error state
    if (pickupData && pickupData.error) {
      return { error: true };
    }
    
    if (pickupData) {
      return calculatePickupTimeText(pickupData);
    }

    return null;
  }

  /**
   * Get pickup time from configured availability data
   */
  function getConfiguredPickupTime(availability) {
    if (availability.pick_up_time && availability.pick_up_time.trim() !== "") {
      return {
        text: availability.pick_up_time,
        availableToday: isPickupAvailableToday(availability.pick_up_time)
      };
    }
    return null;
  }

  /**
   * Determine pickup availability and time text
   */
  async function determinePickupAvailability(availability) {
    const defaultPickup = {
      text: "Usually ready in 2-3 hours",
      availableToday: true
    };

    if (!availability) {
      return defaultPickup;
    }

    // Check if pickup is enabled at this location
    if (!availability.pick_up_enabled) {
      return { text: "Pickup not available at this location", availableToday: false };
    }

    // Check if product variant is available (in stock) at this location
    if (!availability.available) {
      return { text: "Out of Stock", availableToday: false };
    }

    // Product is available and pickup is enabled - get time estimate
    // Try API first, then fall back to configured time
    const apiTime = await getPickupTime(availability);
    
    // If API returned an error, show error message
    if (apiTime && apiTime.error) {
      return { text: "Pickup not available at this location", availableToday: false, apiError: true };
    }
    
    if (apiTime) {
      return apiTime;
    }

    const configuredTime = getConfiguredPickupTime(availability);
    if (configuredTime) {
      return configuredTime;
    }

    return defaultPickup;
  }

  /**
   * Update pickup title element
   */
  function updatePickupTitle(store, pickupTimeText, availableToday, apiError) {
    if (!pickupTitle) {
      return;
    }

    const storeName = decodeHtmlEntities(store.name);
    const storeNameEl = `<strong class="pickup__store-name" style="cursor: pointer;">${storeName}</strong>`;

    if (pickupTimeText === "Out of Stock") {
      pickupTitle.innerHTML = `Not Available at ${storeNameEl}`;
    } else if (apiError || pickupTimeText === "Pickup not available at this location") {
      pickupTitle.textContent = "Pickup not available at this location";
    } else if (availableToday) {
      pickupTitle.innerHTML = `Pickup Today At ${storeNameEl}`;
    } else {
      pickupTitle.innerHTML = `Pickup at ${storeNameEl}`;
    }

    // Add click handler to the store name
    const storeNameElement = pickupTitle.querySelector(".pickup__store-name");
    if (storeNameElement) {
      storeNameElement.addEventListener("click", openStoreSelector);
    }
  }

  /**
   * Update pickup time element
   */
  function updatePickupTimeElement(pickupTimeText, hoursUnavailable) {
    if (!pickupTime) {
      return;
    }

    pickupTime.innerHTML = pickupTimeText;

    if (pickupTimeText === "Out of Stock") {
      pickupTime.classList.add("pickup__time--out-of-stock");
    } else {
      pickupTime.classList.remove("pickup__time--out-of-stock");
    }

    // green copy when in stock but Google hours are not yet available
    if (hoursUnavailable) {
      pickupTime.classList.add("pickup__time--hours-unavailable");
    } else {
      pickupTime.classList.remove("pickup__time--hours-unavailable");
    }
  }

  /**
   * Update pickup section with selected store
   */
  async function updatePickupSection(store) {
    if (!store) {
      resetPickupSection();
      return;
    }

    const availability = getStoreAvailability(store);
    
    // If we can't get store availability data, show fallback messaging
    if (!availability) {
      resetPickupSection();
      return;
    }

    const pickupInfo = await determinePickupAvailability(availability);
    const { text: pickupTimeText, availableToday, apiError, hoursUnavailable } = pickupInfo;

    updatePickupTitle(store, pickupTimeText, availableToday, apiError);
    
    // Don't show time text if there's an API error
    if (apiError) {
      if (pickupTime) {
        pickupTime.textContent = "";
      }
    } else {
      updatePickupTimeElement(pickupTimeText, hoursUnavailable);
    }

    if (findStoreBtn) {
      findStoreBtn.textContent = "Change Store";
    }
  }

  // ==================== API FUNCTIONS ====================

  /**
   * Fetch delivery messaging configurations from API with caching
   * @returns {Promise<Array>} Array of delivery messaging configurations
   */
  async function fetchDeliveryMessaging() {
    if (deliveryMessagingCache) {
      return deliveryMessagingCache;
    }

    try {
      const response = await fetch(CONFIG.BASE_URL + CONFIG.ESTIMATED_DELIVERY_MESSAGING_ROUTE);
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      deliveryMessagingCache = data.estimatedDeliveryMessaging || [];
      
      return deliveryMessagingCache;
    } catch (error) {
      console.error("Enhanced Delivery Estimate: Failed to fetch delivery messaging", error);
      return [];
    }
  }

  // ==================== MATCHING LOGIC ====================

  /**
   * Check if platform matches configuration
   * @param {Array} configPlatforms - Platforms from config
   * @param {string} targetPlatform - Target platform to match
   * @returns {boolean} True if platform matches
   */
  function isPlatformMatch(configPlatforms, targetPlatform) {
    // If platform is not specified in config, it matches all platforms
    if (!configPlatforms || configPlatforms.length === 0) {
      return true;
    }
    return configPlatforms.some(
      platform => platform.toLowerCase() === targetPlatform.toLowerCase()
    );
  }

  /**
   * Check if product type matches configuration
   * @param {string} configProductType - Product type from config
   * @param {string} normalizedProductType - Normalized product type
   * @returns {boolean} True if product type matches
   */
  function isProductTypeMatch(configProductType, normalizedProductType) {
    if (!configProductType) {
      return true;
    }
    return configProductType.toLowerCase() === normalizedProductType;
  }

  /**
   * Check if conveyance matches configuration
   * @param {string} configConveyance - Conveyance from config
   * @param {string} normalizedConveyance - Normalized conveyance (should be "digital" or "physical")
   * @param {string} productType - Product type (for custom frame special handling)
   * @returns {boolean} True if conveyance matches
   */
  function isConveyanceMatch(configConveyance, normalizedConveyance, productType) {
    if (!configConveyance) {
      return true;
    }
    
    // For custom frames, accept both digital and physical conveyances
    if (productType === "custom frame") {
      const configLower = configConveyance.toLowerCase();
      return configLower === "digital" || configLower === "physical";
    }
    
    const configLower = configConveyance.toLowerCase();
    
    // Direct match (like Vue component: messaging.conveyance?.toLowerCase() === digitalOrPhysical)
    return configLower === normalizedConveyance;
  }

  /**
   * Check if production category matches configuration
   * @param {Object} config - Configuration object
   * @param {string} normalizedConveyance - Normalized conveyance
   * @param {string} normalizedProductionCategory - Normalized production category
   * @returns {boolean} True if production category matches
   */
  function isProductionCategoryMatch(config, normalizedConveyance, normalizedProductionCategory) {
    const categoryField = normalizedConveyance === "digital" 
      ? "digitalProductionCategories" 
      : "physicalProductionCategories";
    
    const categories = config[categoryField];
    if (!categories || categories.length === 0) {
      return true;
    }
    
    const normalizedCategories = categories.map(cat => cat.toLowerCase());
    return normalizedCategories.includes(normalizedProductionCategory);
  }

  /**
   * Check if current date is within configuration date range
   * @param {Object} config - Configuration object
   * @param {Date} currentDate - Current date
   * @returns {boolean} True if date is within range
   */
  function isDateInRange(config, currentDate) {
    if (!config.startDateTime || !config.endDateTime) {
      return true;
    }
    
    const startDate = new Date(config.startDateTime);
    const endDate = new Date(config.endDateTime);
    
    // Inclusive start, exclusive end (endDateTime is typically "07:59:00Z" meaning end of previous day)
    return currentDate >= startDate && currentDate < endDate;
  }

  /**
   * Determine SLA type from matched messaging configuration
   * This should be called AFTER matching the messaging config
   * The matched config contains the slaType that should be used
   * @param {Object} matchedMessaging - The matched messaging configuration
   * @returns {string} SLA type ("holiday", "evergreen", etc.)
   */
  function determineSlaTypeFromMessaging(matchedMessaging) {
    if (!matchedMessaging || !matchedMessaging.slaType) {
      return "evergreen";
    }
    return matchedMessaging.slaType.toLowerCase();
  }

  /**
   * Determine conveyance type based on artwork type (like Vue component)
   * @param {Object} deliveryEstimateOptions - The delivery estimate options from API
   * @returns {string} Conveyance type ("digital" or "physical")
   */
  function determineConveyanceFromEstimate(deliveryEstimateOptions) {
    if (!deliveryEstimateOptions) {
      return "digital";
    }
    
    const hasDigital = deliveryEstimateOptions.digital && Object.keys(deliveryEstimateOptions.digital).length > 0;
    const hasPhysical = deliveryEstimateOptions.physical && Object.keys(deliveryEstimateOptions.physical).length > 0;
    
    if (productType === "custom frame" && hasPhysical) {
      return "physical";
    }
    
    // For curated design frames and frame bundles, prefer digital if available
    if ((productType === "curated design frame" || productType === "frame bundle") && hasDigital) {
      return "digital";
    }
    
    // Otherwise, prefer digital, then physical as fallback
    if (hasDigital) {
      return "digital";
    } else if (hasPhysical) {
      return "physical";
    }
    
    return "digital";
  }

  /**
   * Match delivery messaging configuration based on product context and date
   * @param {Array} messagingConfigs - Array of messaging configurations from API
   * @returns {Object|null} Matched configuration or null
   */
  function matchDeliveryMessaging(messagingConfigs) {
    
    if (!messagingConfigs || messagingConfigs.length === 0) {
      console.error("❌ No messaging configs provided");
      return null;
    }

    const now = new Date();
    const normalizedProductType = productType?.toLowerCase() || "";
    
    // Use the determined conveyance (like Vue component)
    // Note: SLA type matching is different - we DON'T pre-determine it from API
    // Instead, we match configs without SLA filtering, then use the matched config's slaType
    const actualConveyance = deliveryData ? determineConveyanceFromEstimate(deliveryData) : conveyance?.toLowerCase() || "";
    const normalizedProductionCategory = productionCategory?.toLowerCase() || "";

    const matchedConfigs = messagingConfigs.filter((config, index) => {
      
      // Platform match - if platform field doesn't exist, assume it matches (new API structure)
      const platformMatch = config.platform ? isPlatformMatch(config.platform, CONFIG.PLATFORM) : true;
      const productTypeMatch = isProductTypeMatch(config.shopifyProductType, normalizedProductType);
      const conveyanceMatch = isConveyanceMatch(config.conveyance, actualConveyance, normalizedProductType);
      const productionCategoryMatch = isProductionCategoryMatch(
        config, actualConveyance, normalizedProductionCategory
      );
      const dateMatch = isDateInRange(config, now);
      
      // IMPORTANT: Don't filter by SLA type during matching
      // The matched config will tell us which SLA type to use
      // This matches the Vue component behavior

      // Check overall match (WITHOUT slaTypeMatch)
      const overallMatch = productTypeMatch && conveyanceMatch &&
        productionCategoryMatch && dateMatch && platformMatch;

      return overallMatch;
    });

    // Find the first match that has pdpContent
    if (matchedConfigs.length > 0) {
      const configWithPdpContent = matchedConfigs.find(config => config.pdpContent);
      if (configWithPdpContent) {
        return configWithPdpContent;
      } else {
        console.error("❌ Matched configs found but NONE have pdpContent");
        return null;
      }
    }
    
    console.error("❌ No matched configs found");
    return null;
  }

  /**
   * Match the Frame Review messaging configuration for the conveyance selected in the dropdown
   * @param {Array} messagingConfigs - Array of messaging configurations from API
   * @returns {Object|null} Matched configuration or null
   */
  function matchSelectedConveyanceMessaging(messagingConfigs) {
    if (!messagingConfigs || !selectedConveyance) {
      return null;
    }

    const now = new Date();
    const normalizedProductType = productType?.toLowerCase() || "";

    return messagingConfigs.find(config =>
      config.conveyance?.toLowerCase() === selectedConveyance &&
      isProductTypeMatch(config.shopifyProductType, normalizedProductType) &&
      isDateInRange(config, now) &&
      config.frameReviewContent?.length > 0
    ) || null;
  }

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Format date string for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date (e.g., "Dec 10")
   */
  function formatDate(dateString) {
    if (!dateString) {
      return "";
    }
    
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", CONFIG.DATE_FORMAT);
  }

  /**
   * Calculate expedited delivery date (1-day shipping)
   * @param {string} zipcode - Destination zip code
   * @returns {Promise<string>} Formatted date string
   */
  async function calculateExpeditedOneDay(zipcode) {
    try {
      const deliveryComposable = useDelivery();
      
      // Use the new options endpoint to get expedited estimates
      const options = {
        zipcode: zipcode,
        quickShip: hasQuickShip,
        readymade: hasTabletopFrames,
        customerPackaging: deliveryComposable.customerPackaging.value,
        postPurchaseDesignConsultation: deliveryComposable.postPurchaseDesignConsultation.value
      };

      const deliveryEstimateOptions = await deliveryComposable.fetchDeliveryEstimateOptions(options);
      
      if (deliveryEstimateOptions?.digital) {
        const digitalEstimate = deliveryEstimateOptions.digital;
        
        // Check if there are expedited options available
        if (digitalEstimate.expedited) {
          const date = digitalEstimate.expedited.estimatedDeliveryDate;
          return formatDate(date);
        }
        
        // Fallback to overnight if available
        if (digitalEstimate.overnight) {
          const date = digitalEstimate.overnight.estimatedDeliveryDate;
          return formatDate(date);
        }
      }
    } catch (error) {
      console.error("Error calculating expedited one-day date:", error);
    }
    return "";
  }

  /**
   * Calculate expedited delivery date (2-day shipping)
   * @param {string} zipcode - Destination zip code
   * @returns {Promise<string>} Formatted date string
   */
  async function calculateExpeditedTwoDay(zipcode) {
    try {
      const deliveryComposable = useDelivery();
      
      // Use the new options endpoint to get expedited estimates
      const options = {
        zipcode: zipcode,
        quickShip: hasQuickShip,
        readymade: hasTabletopFrames,
        customerPackaging: deliveryComposable.customerPackaging.value,
        postPurchaseDesignConsultation: deliveryComposable.postPurchaseDesignConsultation.value
      };

      const deliveryEstimateOptions = await deliveryComposable.fetchDeliveryEstimateOptions(options);
      
      if (deliveryEstimateOptions?.digital?.expedited) {
        const date = deliveryEstimateOptions.digital.expedited.estimatedDeliveryDate;
        return formatDate(date);
      }
    } catch (error) {
      console.error("Error calculating expedited two-day date:", error);
    }
    return "";
  }

  /**
   * Get mail-in date for custom frames
   * @returns {Promise<string>} Formatted date string
   */
  async function getMailInDate() {
    if (!deliveryData) {
      return "";
    }

    // Options endpoint has no mailin key; ship-art-by date lives on the physical estimate (as in Frame Review)
    if (!deliveryData.mailin) {
      const useHoliday = (currentSlaType || "evergreen").toLowerCase() === "holiday";
      const physicalEstimate = useHoliday
        ? (deliveryData.physical?.holiday || deliveryData.physical?.evergreen)
        : (deliveryData.physical?.evergreen || deliveryData.physical?.holiday);

      return formatDate(physicalEstimate?.shipArtByDate);
    }

    // Handle both old and new data structures
    const mailinEstimate = deliveryData.mailin.deliveryEstimate || deliveryData.mailin;
    if (mailinEstimate && mailinEstimate.shipArtByDate) {
      return formatDate(mailinEstimate.shipArtByDate);
    }
    return "";
  }

  /**
   * Get drop-off date for custom frames
   * @returns {Promise<string>} Formatted date string
   */
  async function getDropOffDate() {
    // For now, use same as mail-in date (can be adjusted if different logic needed)
    return getMailInDate();
  }

  /**
   * Get digital delivery date for custom frames
   * @returns {string} Formatted date string
   */
  function getDigitalDate() {
    if (!deliveryData) {
      return "";
    }

    // Use SLA type from matched messaging config
    const slaType = currentSlaType || "evergreen";
    const useHoliday = slaType.toLowerCase() === "holiday";
    
    const digitalEstimate = useHoliday 
      ? (deliveryData.digital?.holiday || deliveryData.digital?.evergreen)
      : (deliveryData.digital?.evergreen || deliveryData.digital?.holiday);
      
    const digitalDate = digitalEstimate?.estimatedDeliveryDate;
    return formatDate(digitalDate);
  }

  /**
   * Get physical delivery date for custom frames
   * @returns {string} Formatted date string
   */
  function getPhysicalDate() {
    if (!deliveryData) {
      return "";
    }

    // Use SLA type from matched messaging config
    const slaType = currentSlaType || "evergreen";
    const useHoliday = slaType.toLowerCase() === "holiday";
    
    const physicalEstimate = useHoliday
      ? (deliveryData.physical?.holiday || deliveryData.physical?.evergreen)
      : (deliveryData.physical?.evergreen || deliveryData.physical?.holiday);
      
    const physicalDate = physicalEstimate?.estimatedDeliveryDate;
    return formatDate(physicalDate);
  }

  /**
   * Get base estimate for token replacement
   * @returns {Object} Base estimate object
   */
  function getBaseEstimate() {
    const actualConveyance = renderedConveyance || determineConveyanceFromEstimate(deliveryData);
    const slaType = currentSlaType || "evergreen";
    const useHoliday = slaType.toLowerCase() === "holiday";
    
    if (actualConveyance === "digital" && deliveryData?.digital) {
      return useHoliday 
        ? (deliveryData.digital.holiday || deliveryData.digital.evergreen)
        : (deliveryData.digital.evergreen || deliveryData.digital.holiday);
    } else if (actualConveyance === "physical" && deliveryData?.physical) {
      return useHoliday
        ? (deliveryData.physical.holiday || deliveryData.physical.evergreen)
        : (deliveryData.physical.evergreen || deliveryData.physical.holiday);
    }
    
    // Fallback to old structure
    return deliveryData?.evergreen || deliveryData?.holiday;
  }

  /**
   * Replace expedited date tokens
   * @param {string} result - Text to process
   * @param {string} zipcode - Current zip code
   * @returns {Promise<string>} Text with expedited tokens replaced
   */
  async function replaceExpeditedTokens(result, zipcode) {
    if (result.includes("{dateExpeditedOneDay}")) {
      const oneDayDate = await calculateExpeditedOneDay(zipcode);
      result = result.replace(/\{dateExpeditedOneDay\}/gi, oneDayDate);
    }

    if (result.includes("{dateExpeditedTwoDay}")) {
      const twoDayDate = await calculateExpeditedTwoDay(zipcode);
      result = result.replace(/\{dateExpeditedTwoDay\}/gi, twoDayDate);
    }

    return result;
  }

  /**
   * Replace custom frame specific tokens
   * @param {string} result - Text to process
   * @returns {Promise<string>} Text with custom frame tokens replaced
   */
  async function replaceCustomFrameTokens(result) {
    if (result.includes("{dateMailIn}")) {
      const mailInDate = await getMailInDate();
      result = result.replace(/\{dateMailIn\}/gi, mailInDate);
    }

    if (result.includes("{dateDropOff}")) {
      const dropOffDate = await getDropOffDate();
      result = result.replace(/\{dateDropOff\}/gi, dropOffDate);
    }

    if (result.includes("{dateDigital}")) {
      const digitalDate = getDigitalDate();
      result = result.replace(/\{dateDigital\}/gi, digitalDate);
    }

    if (result.includes("{datePhysical}")) {
      const physicalDate = getPhysicalDate();
      result = result.replace(/\{datePhysical\}/gi, physicalDate);
    }

    return result;
  }

  /**
   * Replace date tokens in messaging copy
   * @param {string} text - Text containing date tokens
   * @param {string} zipcode - Current zip code
   * @returns {Promise<string>} Text with tokens replaced
   */
  async function replaceDateTokens(text, zipcode) {
    if (!text || typeof text !== "string") {
      return text;
    }

    const baseEstimate = getBaseEstimate();
    const baseDate = baseEstimate?.estimatedDeliveryDate;
    const baseDateFormatted = formatDate(baseDate);

    let result = text;
    
    // Replace basic tokens - support both {DATE} and {date} for backwards compatibility
    result = result.replace(/\{DATE\}/gi, baseDateFormatted);
    result = result.replace(/\{date\}/gi, baseDateFormatted);
    result = result.replace(/\{zip\}/gi, `Shipping to __${zipcode}__`);

    // Replace expedited tokens
    result = await replaceExpeditedTokens(result, zipcode);

    // Replace custom frame specific tokens
    result = await replaceCustomFrameTokens(result);

    return result;
  }

  /**
   * Show specific scenario and hide all others
   * @param {string} scenarioName - Name of scenario to show
   */
  function showScenario(scenarioName) {
    // Hide all scenarios
    Object.values(scenarios).forEach(scenario => {
      if (scenario) {
        scenario.classList.add(CSS_CLASSES.HIDE);
      }
    });
 
    // Show selected scenario
    const targetScenario = scenarios[scenarioName];
    
    if (targetScenario) {
      targetScenario.classList.remove(CSS_CLASSES.HIDE);
      component.classList.remove(CSS_CLASSES.HIDE);
    } else {
      console.error("❌ Target scenario not found:", scenarioName);
    }

    // Handle pickup section visibility and container width
    // Hide pickup and make container full-width for noZip, zipInput, and customFrameDetails scenarios
    // Also hide pickup if product is not in-store fulfillable
    if (scenarioName === "noZip" || scenarioName === "zipInput" || scenarioName === "customFrameDetails" || !inStoreFulfillable) {
      if (pickupSection) {
        pickupSection.classList.add(CSS_CLASSES.HIDE);
      }
      if (personalizedMessageSection) {
        personalizedMessageSection.classList.add(CSS_CLASSES.HIDE);
      }
      if (containerSection) {
        containerSection.classList.add("enhanced-delivery-estimate__container--full-width");
      }
    } else {
      // Show pickup or personalized message based on product type (only if in-store fulfillable)
      if (isPersonalizedTabletop) {
        // Show personalized message, hide pickup
        if (pickupSection) {
          pickupSection.classList.add(CSS_CLASSES.HIDE);
        }
        if (personalizedMessageSection) {
          personalizedMessageSection.classList.remove(CSS_CLASSES.HIDE);
        }
      } else {
        // Show pickup, hide personalized message
        if (pickupSection) {
          pickupSection.classList.remove(CSS_CLASSES.HIDE);
        }
        if (personalizedMessageSection) {
          personalizedMessageSection.classList.add(CSS_CLASSES.HIDE);
        }
      }
      if (containerSection) {
        containerSection.classList.remove("enhanced-delivery-estimate__container--full-width");
      }
    }
  }


  /**
   * Update icon from API asset
   * @param {HTMLElement} iconContainer - Icon container element
   * @param {string} iconAssetUrl - URL of the icon asset
   */
  function updateIcon(iconContainer, iconAssetUrl) {
    if (!iconContainer || !iconAssetUrl) {
      return;
    }

    // Check if icon asset is empty string - hide icon
    if (iconAssetUrl === "") {
      iconContainer.style.display = "none";
      return;
    }

    // Show icon container
    iconContainer.style.display = "";

    // Replace SVG with img element
    const imgElement = document.createElement("img");
    imgElement.src = iconAssetUrl;
    imgElement.alt = "Delivery icon";
    imgElement.style.width = "26px";
    imgElement.style.height = "26px";
    
    // Clear existing content and add image
    iconContainer.innerHTML = "";
    iconContainer.appendChild(imgElement);
  }

  /**
   * Get current zipcode from DOM elements
   * @returns {string|null} Current zipcode or null
   */
  function getCurrentZipcode() {
    if (elements.currentZipcode?.textContent) {
      return elements.currentZipcode.textContent.trim();
    }
    if (elements.customFrameCurrentZipcode?.textContent) {
      return elements.customFrameCurrentZipcode.textContent.trim();
    }
    return null;
  }

  /**
   * Extract delivery estimate and marketing message from nested structure
   * The API returns an array like:
   * [
   *   { deliveryEstimates: { iconAsset, eddCopy } },
   *   { marketingMessage: { marketingCopy, marketingCopyColor } }
   * ]
   * @param {Object|Array} content - Content object or array
   * @returns {Object} Object with deliveryEstimate and marketingMessage properties
   */
  function extractDeliveryEstimate(content) {
    let deliveryEstimate = null;
    let marketingMessage = null;

    if (Array.isArray(content)) {
      // Loop through array to find deliveryEstimates and marketingMessage
      content.forEach(item => {
        if (item.deliveryEstimates) {
          deliveryEstimate = item.deliveryEstimates;
        }
        if (item.marketingMessage) {
          marketingMessage = item.marketingMessage;
        }
      });
    } else if (content.deliveryEstimates || content.marketingMessage) {
      // Single object with properties
      deliveryEstimate = content.deliveryEstimates || content;
      marketingMessage = content.marketingMessage;
    } else {
      // Fallback - treat as single delivery estimate
      deliveryEstimate = content;
    }

    return { deliveryEstimate, marketingMessage };
  }

  /**
   * Render dynamic content from API messaging
   * @param {Object} messagingConfig - Matched messaging configuration
   * @param {string} zipcode - Current zip code
   */
  async function renderDynamicContent(messagingConfig, zipcode) {
    try {
      if (!messagingConfig || !messagingConfig.pdpContent) {
        return false;
      }

      const pdpContent = messagingConfig.pdpContent;
      
      // Extract delivery estimate and marketing message using helper function
      const { deliveryEstimate, marketingMessage: extractedMarketingMessage } = extractDeliveryEstimate(pdpContent);

      // Update icon if provided
      if (deliveryEstimate?.iconAsset !== undefined) {
        const iconContainer = document.getElementById("deliveryIconWithZip");
        updateIcon(iconContainer, deliveryEstimate.iconAsset);
      }

      // Get or create content container
      const contentContainer = scenarios.withZip?.querySelector(".delivery-estimate__content");
      if (!contentContainer) {
        return false;
      }

      // Clear existing content
      contentContainer.innerHTML = "";

      // Create main content wrapper
      const mainDiv = document.createElement("div");
      mainDiv.className = "delivery-estimate__main";

      // Update EDD copy with token replacement
      if (deliveryEstimate?.eddCopy) {
        const eddText = await replaceDateTokens(deliveryEstimate.eddCopy, zipcode);
        
        const titleElement = document.createElement("h3");
        titleElement.className = "delivery-estimate__title";
        titleElement.innerHTML = eddText;
        mainDiv.appendChild(titleElement);
      }

      // Add zipcode display
      const shippingElement = document.createElement("p");
      shippingElement.className = "delivery-estimate__shipping";
      shippingElement.innerHTML = `Shipping to <button id="zipcodeButton" class="delivery-estimate__zipcode-btn" type="button"><span id="currentZipcode">${zipcode}</span></button>`;
      mainDiv.appendChild(shippingElement);

      // Add marketing messages if present
      if (extractedMarketingMessage) {
        const messages = Array.isArray(extractedMarketingMessage) 
          ? extractedMarketingMessage 
          : [extractedMarketingMessage];
        
        for (const message of messages) {
          if (message.marketingCopy) {
            const marketingText = await replaceDateTokens(message.marketingCopy, zipcode);
            
            // Create marketing message element
            const marketingElement = document.createElement("p");
            marketingElement.className = "delivery-estimate__free-shipping";
            marketingElement.innerHTML = marketingText;
            
            if (message.marketingCopyColor) {
              marketingElement.style.color = message.marketingCopyColor;
            }
            
            // Ensure anchor tags inherit the same styling
            const anchors = marketingElement.querySelectorAll("a");
            anchors.forEach(anchor => {
              anchor.style.fontSize = "inherit";
              anchor.style.lineHeight = "inherit";
              anchor.style.letterSpacing = "inherit";
              anchor.style.fontFamily = "inherit";
              anchor.style.fontWeight = "700";
              anchor.style.textDecoration = "underline";
              anchor.style.color = "inherit";
            });
            
            mainDiv.appendChild(marketingElement);
          }
        }
      }

    // Append main content to container
    contentContainer.appendChild(mainDiv);

      // Re-attach event listener for zipcode button
      const zipcodeBtn = document.getElementById("zipcodeButton");
      if (zipcodeBtn) {
        zipcodeBtn.addEventListener("click", () => {
          showScenario("zipInput");
        });
      }

      return true;
    } catch (error) {
      console.error("Enhanced Delivery Estimate: Error rendering pdpContent", error);
      return false;
    }
  }

  async function displayDeliveryEstimate(zipcode) {
    if (!deliveryData) {
      component.classList.add(CSS_CLASSES.HIDE);
      return;
    }


    if (elements.currentZipcode) {
      elements.currentZipcode.textContent = zipcode;
    }

    // Fetch and match dynamic messaging
    const messagingConfigs = await fetchDeliveryMessaging();
    const matchedMessaging = matchDeliveryMessaging(messagingConfigs);
    
    // Cache matched messaging for later use (e.g., details view)
    cachedMatchedMessaging = matchedMessaging;

    // If no matching messaging configuration, hide the entire component
    if (!matchedMessaging) {
      component.classList.add(CSS_CLASSES.HIDE);
      return;
    }

    // Try to render dynamic content
    // Show scenario first so DOM elements are available
    showScenario(SCENARIOS.WITH_ZIP);
    
    const rendered = await renderDynamicContent(matchedMessaging, zipcode);
    if (rendered) {
      return;
    }
    
    // If rendering failed, hide the component
    component.classList.add(CSS_CLASSES.HIDE);
  }

  /**
   * Validate delivery data exists for custom frame
   */
  function validateCustomFrameData() {
    if (!deliveryData || !deliveryData.digital || !deliveryData.physical) {
      return null;
    }

    // Check if the digital/physical objects are empty (new API issue)
    const digitalKeys = Object.keys(deliveryData.digital || {});
    const physicalKeys = Object.keys(deliveryData.physical || {});

    // If both objects are empty, the API didn't return proper data
    if (digitalKeys.length === 0 && physicalKeys.length === 0) {
      return null;
    }

    // Use SLA type from matched messaging config (like Vue component)
    const slaType = currentSlaType || "evergreen";
    const useHoliday = slaType.toLowerCase() === "holiday";
    
    // Select estimates based on SLA type (like Vue component logic)
    const digitalEstimate = useHoliday 
      ? (deliveryData.digital?.holiday || deliveryData.digital?.evergreen)
      : (deliveryData.digital?.evergreen || deliveryData.digital?.holiday);
      
    const physicalEstimate = useHoliday
      ? (deliveryData.physical?.holiday || deliveryData.physical?.evergreen)
      : (deliveryData.physical?.evergreen || deliveryData.physical?.holiday);

    // Check if the processed estimates have actual data
    const digitalHasData = digitalEstimate && Object.keys(digitalEstimate).length > 0;
    const physicalHasData = physicalEstimate && Object.keys(physicalEstimate).length > 0;

    if (!digitalHasData || !physicalHasData) {
      return null;
    }

    return { digitalEstimate, physicalEstimate };
  }


  /**
   * Populate detail fields from API responses
   */
  async function populateCustomFrameDetails(digitalEstimate, physicalEstimate) {
    // New API structure
    const physicalDeliveryDate = physicalEstimate?.estimatedDeliveryDate;
    const digitalDeliveryDate = digitalEstimate?.estimatedDeliveryDate;

    if (digitalDeliveryDate) {
      if (elements.detailsDigitalDate) {
        elements.detailsDigitalDate.textContent = formatDate(digitalDeliveryDate);
      }

      if (elements.detailsDigitalExpedited) {
        // Check if expedited data is available in the digital estimate
        let digitalExpeditedDate = null;
        
        // Look for expedited options in the digital delivery data
        if (deliveryData.digital?.expedited) {
          digitalExpeditedDate = deliveryData.digital.expedited.estimatedDeliveryDate;
        } else if (deliveryData.digital?.overnight) {
          digitalExpeditedDate = deliveryData.digital.overnight.estimatedDeliveryDate;
        }
        
        if (digitalExpeditedDate) {
          elements.detailsDigitalExpedited.textContent = formatDate(digitalExpeditedDate);
        } else {
          // Fallback to non-expedited date
          elements.detailsDigitalExpedited.textContent = formatDate(digitalDeliveryDate);
        }
      }
      
      if (elements.detailsPhysicalDate) {
        elements.detailsPhysicalDate.textContent = formatDate(physicalDeliveryDate);
      }
    }
    
    const shipArtByDate = physicalEstimate.shipArtByDate;
    if (shipArtByDate && elements.detailsMailInDate) {
      elements.detailsMailInDate.textContent = formatDate(shipArtByDate);
    }
  }

  /**
   * Create delivery details HTML for custom frame details
   */
  function createCustomFrameDetailsHTML(digitalDate, mailinDate, mailInByDate) {
    let detailsHTML = "";
    
    // Digital Uploads line
    if (digitalDate) {
      const formattedDigitalDate = formatDate(digitalDate);
      detailsHTML += `<strong style="font-weight: 700;">Digital Uploads:</strong> Estimated delivery on ${formattedDigitalDate} with UPS Ground ®<br><br>`;
    }
    
    // Physical Art line
    if (mailinDate && mailInByDate) {
      const formattedMailinDate = formatDate(mailinDate);
      const formattedMailInByDate = formatDate(mailInByDate);
      detailsHTML += `<strong style="font-weight: 700;">Physical Art:</strong> Estimated delivery on ${formattedMailinDate} when you send in your art or drop it off at a store by ${formattedMailInByDate}`;
    }
    
    return detailsHTML;
  }

  /**
   * Apply styling to marketing element
   */
  function applyMarketingElementStyling(marketingElement, message) {
    marketingElement.style.fontFamily = "Apercu Pro, sans-serif";
    marketingElement.style.fontSize = "12px";
    marketingElement.style.fontWeight = "400";
    marketingElement.style.lineHeight = "18px";
    marketingElement.style.letterSpacing = "0.24px";
    marketingElement.style.marginBottom = "8px";
    marketingElement.style.marginTop = "0";
    
    // Apply custom color from API or default to black
    if (message.marketingCopyColor) {
      marketingElement.style.color = message.marketingCopyColor;
    } else {
      marketingElement.style.color = "#1D2019";
    }
    
    // Ensure anchor tags inherit the same styling
    const anchors = marketingElement.querySelectorAll("a");
    anchors.forEach(anchor => {
      anchor.style.fontSize = "12px";
      anchor.style.lineHeight = "18px";
      anchor.style.letterSpacing = "0.24px";
      anchor.style.fontFamily = "Apercu Pro, sans-serif";
      anchor.style.fontWeight = "700";
      anchor.style.textDecoration = "underline";
      anchor.style.color = "inherit";
    });
  }

  /**
   * Create shipping element for custom frame details
   */
  function createShippingElement(zipcode) {
    const shippingElement = document.createElement("p");
    shippingElement.className = "delivery-estimate__shipping";
    shippingElement.style.fontFamily = "Apercu Pro, sans-serif";
    shippingElement.style.fontSize = "12px";
    shippingElement.style.fontWeight = "400";
    shippingElement.style.lineHeight = "18px";
    shippingElement.style.letterSpacing = "0.24px";
    shippingElement.style.color = "#1D2019";
    shippingElement.style.marginBottom = "8px";
    shippingElement.style.marginTop = "0";
    shippingElement.innerHTML = `Shipping to <button id="customFrameDetailsZipcodeButton" class="delivery-estimate__zipcode-btn" type="button"><span id="customFrameDetailsCurrentZipcode">${zipcode}</span></button>`;
    return shippingElement;
  }

  /**
   * Get delivery estimates from delivery data
   */
  function getDeliveryEstimates() {
    // Use SLA type from matched messaging config (like Vue component)
    const slaType = currentSlaType || "evergreen";
    const useHoliday = slaType.toLowerCase() === "holiday";
    
    // Select estimates based on SLA type (like Vue component logic)
    const digitalEstimate = useHoliday 
      ? (deliveryData?.digital?.holiday || deliveryData?.digital?.evergreen)
      : (deliveryData?.digital?.evergreen || deliveryData?.digital?.holiday);
      
    const physicalEstimate = useHoliday
      ? (deliveryData?.physical?.holiday || deliveryData?.physical?.evergreen)
      : (deliveryData?.physical?.evergreen || deliveryData?.physical?.holiday);
                          
    const digitalDate = digitalEstimate?.estimatedDeliveryDate;
    const physicalDate = physicalEstimate?.estimatedDeliveryDate;
    const mailInByDate = physicalEstimate?.shipArtByDate;

    return { digitalDate, mailinDate: physicalDate, mailInByDate };
  }

  /**
   * Render dynamic content for custom frame details scenario
   * @param {Object} messagingConfig - Matched messaging configuration
   * @param {string} zipcode - Current zip code
   * @returns {Promise<boolean>} Success status
   */
  async function renderCustomFrameDetailsDynamicContent(messagingConfig, zipcode) {
    try {
      if (!messagingConfig) {
        return false;
      }
      
      // Only use pdpContent for custom frame details
      const contentToUse = messagingConfig.pdpContent;
      
      if (!contentToUse) {
        return false;
      }

      const pdpContent = contentToUse;
      
      // Extract delivery estimate and marketing message
      const { deliveryEstimate, marketingMessage: extractedMarketingMessage} = 
        extractDeliveryEstimate(pdpContent);

      // Update icon if provided
      const detailsScenario = scenarios.customFrameDetails;
      const iconContainer = detailsScenario?.querySelector(".delivery-estimate__icon");
      if (iconContainer && deliveryEstimate?.iconAsset !== undefined) {
        updateIcon(iconContainer, deliveryEstimate.iconAsset);
      }

      // Get main content container
      const mainContent = detailsScenario?.querySelector(".delivery-estimate__main");
      if (!mainContent) {
        console.error("Enhanced Delivery Estimate: customFrameDetails main content not found");
        return false;
      }

      // Clear existing content
      mainContent.innerHTML = "";

      // Create detailed delivery information from toolTipCopy (nested in deliveryEstimate)
      if (deliveryEstimate?.toolTipCopy) {
        const tooltipText = await replaceDateTokens(deliveryEstimate.toolTipCopy, zipcode);
        const detailsElement = document.createElement("p");
        detailsElement.className = "delivery-estimate__details-text";
        detailsElement.innerHTML = tooltipText;
        mainContent.appendChild(detailsElement);
      }

      // Add zipcode display
      const shippingElement = createShippingElement(zipcode);
      mainContent.appendChild(shippingElement);

      // Add marketing messages if present
      if (extractedMarketingMessage) {
        const messages = Array.isArray(extractedMarketingMessage) 
          ? extractedMarketingMessage 
          : [extractedMarketingMessage];
        
        for (const message of messages) {
          if (message.marketingCopy) {
            const marketingText = await replaceDateTokens(message.marketingCopy, zipcode);
            
            // Create marketing message element
            const marketingElement = document.createElement("p");
            marketingElement.className = "delivery-estimate__free-shipping";
            marketingElement.innerHTML = marketingText;
            
            // Apply styling using helper function
            applyMarketingElementStyling(marketingElement, message);
            
            mainContent.appendChild(marketingElement);
          }
        }
      }

      // Re-attach event listener for zipcode button
      const detailsZipcodeBtn = document.getElementById("customFrameDetailsZipcodeButton");
      if (detailsZipcodeBtn) {
        detailsZipcodeBtn.addEventListener("click", () => {
          showScenario("zipInput");
        });
      }

      return true;
    } catch (error) {
      console.error("Enhanced Delivery Estimate: Error rendering custom frame details", error);
      return false;
    }
  }


  /**
   * Create custom frame title element
   */
  function createCustomFrameTitle(eddText, dateRange, showInfoButton = true) {
    const titleElement = document.createElement("h3");
    titleElement.className = "delivery-estimate__title";
    titleElement.innerHTML = `<span id="customFrameDeliveryDate">${eddText}${dateRange}</span>${showInfoButton ? `
    <svg id="customFrameInfoButton" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" style="cursor: pointer; margin-left: 4px; vertical-align: middle;">
      <circle cx="7" cy="7" r="6.5" fill="#1D2019" stroke="#1D2019"/>
      <path d="M6.51243 8.71805C6.50098 8.61877 6.49143 8.52331 6.48379 8.43167C6.47997 8.34002 6.47806 8.2522 6.47806 8.1682C6.47806 7.89327 6.51243 7.62217 6.58116 7.35488C6.64989 7.08378 6.75681 6.80312 6.90191 6.51293C6.96682 6.38692 7.07182 6.20364 7.21692 5.96308C7.58349 5.35977 7.76677 4.87102 7.76677 4.49682C7.76677 4.17989 7.68086 3.92979 7.50903 3.7465C7.3372 3.5594 7.10237 3.46585 6.80454 3.46585C6.73581 3.46585 6.66326 3.47349 6.58689 3.48876C6.51052 3.50403 6.43033 3.52695 6.34633 3.55749C6.44561 3.65295 6.52007 3.75414 6.56971 3.86105C6.61935 3.96797 6.64416 4.08634 6.64416 4.21616C6.64416 4.45672 6.55634 4.65337 6.3807 4.80611C6.20505 4.95884 5.97976 5.03521 5.70484 5.03521C5.44519 5.03521 5.23709 4.95884 5.08053 4.80611C4.92398 4.64955 4.8457 4.44527 4.8457 4.19325C4.8457 3.75032 5.04426 3.38948 5.44137 3.11074C5.8423 2.832 6.36351 2.69263 7.005 2.69263C7.70759 2.69263 8.27271 2.86445 8.70037 3.20811C9.12803 3.54795 9.34186 3.99661 9.34186 4.55409C9.34186 4.76792 9.3094 4.97221 9.24449 5.16694C9.17958 5.35786 9.08221 5.53733 8.95238 5.70534C8.80346 5.90771 8.60109 6.13491 8.34526 6.38692C8.09324 6.63893 7.93669 6.80694 7.8756 6.89095C7.68849 7.14296 7.5453 7.41598 7.44603 7.70999C7.34675 8.00019 7.28184 8.33621 7.25129 8.71805H6.51243ZM6.92482 11.3814C6.68426 11.3814 6.47234 11.2916 6.28905 11.1122C6.10959 10.9327 6.01986 10.7227 6.01986 10.4821C6.01986 10.2301 6.10768 10.0144 6.28333 9.83492C6.46279 9.65546 6.67662 9.56573 6.92482 9.56573C7.18065 9.56573 7.39639 9.65355 7.57203 9.8292C7.74768 10.0048 7.8355 10.2225 7.8355 10.4821C7.8355 10.7303 7.74577 10.9423 7.56631 11.1179C7.38684 11.2935 7.17301 11.3814 6.92482 11.3814Z" fill="#FFF9F4"/>
    </svg>` : ""}`;
    return titleElement;
  }


  /**
   * Render dynamic content for custom frames
   * @param {Object} messagingConfig - Matched messaging configuration
   * @param {string} zipcode - Current zip code
   */
  async function renderCustomFrameDynamicContent(messagingConfig, zipcode, contentKey = "pdpContent") {
    try {
      if (!messagingConfig) {
        return false;
      }
      
      // pdpContent for the generic estimate, frameReviewContent once an art type is selected
      const contentToUse = messagingConfig[contentKey];
      const showInfoButton = contentKey === "pdpContent";
      
      if (!contentToUse) {
        return false;
      }

      const pdpContent = contentToUse;
      
      // Extract delivery estimate using helper function
      const { deliveryEstimate, marketingMessage: extractedMarketingMessage } = 
        extractDeliveryEstimate(pdpContent);

      // Update icon if provided
      if (deliveryEstimate?.iconAsset !== undefined) {
        const iconContainer = document.getElementById("deliveryIconCustomFrame");
        updateIcon(iconContainer, deliveryEstimate.iconAsset);
      }

      // Get or create main content container
      const mainContent = document.getElementById("customFrameMainContent");
      if (!mainContent) {
        console.error("Enhanced Delivery Estimate: customFrameMainContent element not found");
        return false;
      }

      // Clear existing content
      mainContent.innerHTML = "";

      // Create title with EDD copy (dates are replaced by token replacement)
      if (deliveryEstimate?.eddCopy) {
        const eddText = await replaceDateTokens(deliveryEstimate.eddCopy, zipcode);
        const titleElement = createCustomFrameTitle(eddText, "", showInfoButton);
        mainContent.appendChild(titleElement);
        
        // Attach event listener to the info button immediately after creating it
        const infoBtn = document.getElementById("customFrameInfoButton");
        if (infoBtn) {
          infoBtn.addEventListener("click", async (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Only render details content once, then just toggle visibility
            if (!detailsRendered && cachedMatchedMessaging && zipcode) {
              const success = await renderCustomFrameDetailsDynamicContent(cachedMatchedMessaging, zipcode);
              if (success) {
                detailsRendered = true;
              }
            }
            showScenario("customFrameDetails");
          });
        }
      }

      // Add zipcode display
      const shippingElement = document.createElement("p");
      shippingElement.className = "delivery-estimate__shipping";
      shippingElement.innerHTML = `Shipping to <button id="customFrameZipcodeButton" class="delivery-estimate__zipcode-btn" type="button"><span id="customFrameCurrentZipcode">${zipcode}</span></button>`;
      mainContent.appendChild(shippingElement);

      // Add marketing messages if present
      if (extractedMarketingMessage) {
        const messages = Array.isArray(extractedMarketingMessage) 
          ? extractedMarketingMessage 
          : [extractedMarketingMessage];
        
        for (const message of messages) {
          if (message.marketingCopy) {
            const marketingText = await replaceDateTokens(message.marketingCopy, zipcode);
            
            // Create marketing message element
            const marketingElement = document.createElement("p");
            marketingElement.className = "delivery-estimate__free-shipping";
            marketingElement.innerHTML = marketingText;
            
            if (message.marketingCopyColor) {
              marketingElement.style.color = message.marketingCopyColor;
            }
            
            // Ensure anchor tags inherit the same styling
            const anchors = marketingElement.querySelectorAll("a");
            anchors.forEach(anchor => {
              anchor.style.fontSize = "inherit";
              anchor.style.lineHeight = "inherit";
              anchor.style.letterSpacing = "inherit";
              anchor.style.fontFamily = "inherit";
              anchor.style.fontWeight = "700";
              anchor.style.textDecoration = "underline";
              anchor.style.color = "inherit";
            });
            
            mainContent.appendChild(marketingElement);
          }
        }
      }

      // Attach event listener for zipcode button
      const zipcodeBtn = document.getElementById("customFrameZipcodeButton");
      if (zipcodeBtn) {
        zipcodeBtn.addEventListener("click", () => {
          showScenario("zipInput");
        });
      }

      // Set tooltip content with date token replacement (toolTipCopy is nested in deliveryEstimate)
      if (deliveryEstimate?.toolTipCopy && elements.tooltip) {
        const tooltipText = await replaceDateTokens(deliveryEstimate.toolTipCopy, zipcode);
        elements.tooltip.innerHTML = tooltipText;
      }

      return true;
    } catch (error) {
      console.error("Enhanced Delivery Estimate: Error rendering pdpContent", error);
      return false;
    }
  }

  /**
   * Display custom frame estimate with digital and mail-in dates
   */
  async function displayCustomFrameEstimate(zipcode) {
    const estimates = validateCustomFrameData();
    
    if (!estimates) {
      console.error("❌ No estimates from validateCustomFrameData, hiding component");
      component.classList.add(CSS_CLASSES.HIDE);
      return;
    }

    const { digitalEstimate, physicalEstimate } = estimates;

    // Extract dates from new API structure
    const digitalDate = digitalEstimate?.estimatedDeliveryDate;
    const physicalDate = physicalEstimate?.estimatedDeliveryDate;
    
    if (!digitalDate || !physicalDate) {
      console.error("❌ Missing digitalDate or physicalDate, hiding component");
      component.classList.add(CSS_CLASSES.HIDE);
      return;
    }

    // Fetch and match dynamic messaging
    const messagingConfigs = await fetchDeliveryMessaging();

    // Once an art type is selected, show its Frame Review estimate; fall back to the generic range if not configured
    const selectedMessaging = matchSelectedConveyanceMessaging(messagingConfigs);

    if (selectedMessaging) {
      renderedConveyance = selectedConveyance;
      currentSlaType = determineSlaTypeFromMessaging(selectedMessaging);
      showScenario("customFrame");

      if (await renderCustomFrameDynamicContent(selectedMessaging, zipcode, "frameReviewContent")) {
        return;
      }
    }

    renderedConveyance = null;
    const matchedMessaging = matchDeliveryMessaging(messagingConfigs);
    
    // If no matching messaging configuration, hide the entire component
    if (!matchedMessaging) {
      console.error("❌ No matched messaging, hiding component");
      component.classList.add(CSS_CLASSES.HIDE);
      return;
    }
    
    // Get SLA type from the matched messaging config (not from API response)
    currentSlaType = determineSlaTypeFromMessaging(matchedMessaging);
    
    // Cache matched messaging for later use (e.g., details view)
    cachedMatchedMessaging = matchedMessaging;

    // Try to render dynamic content
    // Show scenario first so DOM elements are available
    showScenario("customFrame");
    
    const rendered = await renderCustomFrameDynamicContent(matchedMessaging, zipcode);
    
    if (rendered) {
      // Populate details view for when info button is clicked
      await populateCustomFrameDetails(digitalEstimate, physicalEstimate);
      return;
    }
    
    // If rendering failed, hide the component
    component.classList.add(CSS_CLASSES.HIDE);
  }


  /**
   * Fetch delivery data using the new options endpoint
   */
  async function fetchAndDisplayDeliveryData(zipcode) {
    try {
      // Create a new delivery instance with proper configuration
      const deliveryComposable = useDelivery();
      
      // Get product data from component attributes
      const productId = component.dataset.productId;
      
      // Prepare options for the new API call
      const options = {
        zipcode: zipcode,
        quickShip: hasQuickShip,
        readymade: hasTabletopFrames, // Tabletop frames are considered readymade
        customerPackaging: deliveryComposable.customerPackaging.value,
        postPurchaseDesignConsultation: deliveryComposable.postPurchaseDesignConsultation.value
      };

      // Add product-specific parameters if available
      if (productId) {
        // You can add frame_spec_number or other product-specific data here if needed
        // options.frameSpecNumber = getFrameSpecFromProduct(productId);
      }

      // Fetch all delivery estimate options in a single API call
      const deliveryEstimateOptions = await deliveryComposable.fetchDeliveryEstimateOptions(options);
      
      if (!deliveryEstimateOptions) {
        console.error("❌ No deliveryEstimateOptions returned, hiding component");
        component.classList.add(CSS_CLASSES.HIDE);
        return;
      }

      // Process the response based on product type
      if (productType === "custom frame") {
        // For custom frames, we need both digital and physical estimates
        const digitalEstimate = deliveryEstimateOptions.digital;
        const physicalEstimate = deliveryEstimateOptions.physical;
        
        // Check if estimates exist and have data
        const digitalHasData = digitalEstimate && Object.keys(digitalEstimate).length > 0;
        const physicalHasData = physicalEstimate && Object.keys(physicalEstimate).length > 0;
        
        if (digitalHasData && physicalHasData) {
          // Store the full delivery estimate options
          deliveryData = deliveryEstimateOptions;
          
          await displayCustomFrameEstimate(zipcode);
        } else {
          // New API didn't return proper data
          console.error("❌ Missing digital or physical data, hiding component");
          component.classList.add(CSS_CLASSES.HIDE);
        }
        return;
      }

      // For other product types, use the appropriate estimate
      // Priority: digital for most products, physical for mail-in products
      const primaryEstimate = deliveryEstimateOptions.digital || deliveryEstimateOptions.physical;
      
      if (primaryEstimate) {
        // Store the full delivery estimate options
        deliveryData = deliveryEstimateOptions;
        
        await displayDeliveryEstimate(zipcode);
      } else {
        console.error("❌ No primary estimate, hiding component");
        component.classList.add(CSS_CLASSES.HIDE);
      }
    } catch (error) {
      console.error("❌ Error fetching delivery estimate options:", error);
      console.error("Error stack:", error.stack);
      component.classList.add(CSS_CLASSES.HIDE);
    }
  }

  /**
   * Validate ZIP code format (must be 5 digits)
   * @param {string} zipcode - ZIP code to validate
   * @returns {boolean} True if valid
   */
  function isValidZipcode(zipcode) {
    return CONFIG.ZIPCODE_REGEX.test(zipcode);
  }

  /**
   * Show zipcode error
   */
  function showZipcodeError(message) {
    if (elements.zipcodeError) {
      elements.zipcodeError.textContent = message;
      elements.zipcodeError.classList.remove("hide");
    }
  }

  /**
   * Hide zipcode error
   */
  function hideZipcodeError() {
    if (elements.zipcodeError) {
      elements.zipcodeError.classList.add("hide");
    }
  }

  /**
   * Hide modal
   */
  function hideModal() {
    if (elements.modal) {
      elements.modal.classList.add("hide");
      // Clear modal input
      const modalInput = document.getElementById("modalZipcodeInput");
      if (modalInput) {
        modalInput.value = "";
      }
      // Clear modal error
      const modalError = document.getElementById("modalZipcodeError");
      if (modalError) {
        modalError.classList.add("hide");
      }
    }
  }

  /**
   * Handle zip code input submission
   */
  async function handleZipcodeSubmission() {
    const zipcodeValue = elements.zipcodeInput?.value?.trim();
    
    if (!zipcodeValue || !isValidZipcode(zipcodeValue)) {
      showZipcodeError("Please enter a valid ZIP code.");
      return;
    }

    // Show loading state
    if (elements.submitZipcode) {
      elements.submitZipcode.disabled = true;
      elements.submitZipcode.textContent = "Loading...";
    }

    try {
      const { updateZipcode } = useDelivery();
      updateZipcode(zipcodeValue);
      await fetchAndDisplayDeliveryData(zipcodeValue);
      
      // Clear input and hide input scenario
      if (elements.zipcodeInput) {
        elements.zipcodeInput.value = "";
      }
      hideZipcodeError();
      hideModal();
    } catch (error) {
      console.error("Error updating zipcode:", error);
      showZipcodeError("Unable to fetch delivery estimate. Please try again.");
    } finally {
      // Reset button state
      if (elements.submitZipcode) {
        elements.submitZipcode.disabled = false;
        elements.submitZipcode.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path d="M14.3301 5.41L21.6301 12.7L14.3301 20" stroke="white" stroke-width="1.22" stroke-linecap="round" stroke-linejoin="bevel"/>
            <path d="M4 12.71L21.02 12.71" stroke="white" stroke-width="1.22" stroke-linecap="round" stroke-linejoin="bevel"/>
          </svg>
        `;
      }
    }
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Find a Store button - opens store selector modal
    if (findStoreBtn) {
      findStoreBtn.addEventListener("click", openStoreSelector);
    }

    // Listen for clicks on "Select This Store" buttons
    document.body.addEventListener("click", (event) => {
      const storeButton = event.target.closest(".js-set-dropoff-store");
      if (storeButton) {
        // Get the store data from the button
        const storeDataAttr = storeButton.dataset.store;
        if (storeDataAttr) {
          try {
            const storeName = JSON.parse(storeDataAttr);
            
            // Find the full store object from dropOffStores
            const dropOffStoresData = sessionStorage.getItem("dropOffStores");
            if (dropOffStoresData) {
              const stores = JSON.parse(dropOffStoresData);
              const selectedStoreObj = stores.find(store => store.name === storeName);
              
              if (selectedStoreObj) {
                // Update nearestStoresData with the selected store as first item
                sessionStorage.setItem("nearestStoresData", JSON.stringify([selectedStoreObj]));
                
                // Update UI after modal closes
                setTimeout(() => {
                  updatePickupSection(selectedStoreObj);
                }, 600); // Wait for modal close animation
              }
            }
          } catch (error) {
            console.error("Error handling store selection:", error);
          }
        }
      }
    });

    // Listen for storage changes (in case store is selected in another context)
    window.addEventListener("storage", (storageEvent) => {
      if (storageEvent.key === "nearestStoresData") {
        const store = getSelectedStore();
        updatePickupSection(store);
      }
    });

    // Listen for store selection from StoreSelector component
    document.addEventListener("storeSelectedForPickup", (event) => {
      if (event.detail && event.detail.store) {
        const selectedStoreObj = event.detail.store;
        // Save to localStorage for persistence across pages
        localStorage.setItem("selectedStore", JSON.stringify(selectedStoreObj));
        // Update the pickup section
        updatePickupSection(selectedStoreObj);
      }
    });

    // Listen for variant changes to update store availability
    document.addEventListener("variant:changed", (event) => {
      if (event.detail && event.detail.variant) {
        // Variant changed - we would need to fetch new store availability
        // In a full implementation, you'd fetch new availability data via AJAX
        // or the page would refresh with the new variant's availability data
        const store = getSelectedStore();
        if (store) {
          updatePickupSection(store);
        }
      }
    });

    // Enter zipcode button (when no zip is present)
    if (elements.enterZipcodeButton) {
      elements.enterZipcodeButton.addEventListener("click", () => {
        showScenario("zipInput");
      });
    }

    // Zipcode button (to change existing zipcode)
    if (elements.zipcodeButton) {
      elements.zipcodeButton.addEventListener("click", () => {
        showScenario("zipInput");
      });
    }

    // Custom frame zipcode button
    if (elements.customFrameZipcodeButton) {
      elements.customFrameZipcodeButton.addEventListener("click", () => {
        showScenario("zipInput");
      });
    }

    // Custom frame info button - show details scenario
    if (elements.customFrameInfoButton) {
      elements.customFrameInfoButton.addEventListener("click", async () => {
        const zipcode = getCurrentZipcode();
        // Only render details content once, then just toggle visibility
        if (!detailsRendered && zipcode && cachedMatchedMessaging) {
          const success = await renderCustomFrameDetailsDynamicContent(cachedMatchedMessaging, zipcode);
          if (success) {
            detailsRendered = true;
          }
        }
        showScenario("customFrameDetails");
      });
    }

    // Close custom frame details button - go back to custom frame scenario
    if (elements.closeCustomFrameDetails) {
      elements.closeCustomFrameDetails.addEventListener("click", () => {
        showScenario("customFrame");
      });
    }

    // Submit zipcode button in input scenario
    if (elements.submitZipcode) {
      elements.submitZipcode.addEventListener("click", handleZipcodeSubmission);
    }

    // Zipcode input keydown events
    if (elements.zipcodeInput) {
      elements.zipcodeInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          handleZipcodeSubmission();
        }
        // Clear error on input
        hideZipcodeError();
      });

      // Input validation
      elements.zipcodeInput.addEventListener("input", (event) => {
        // Only allow numbers and limit to 5 characters
        event.target.value = event.target.value.replace(/\D/g, "").slice(0, 5);
        hideZipcodeError();
      });
    }

    // Modal close button
    if (elements.closeModal) {
      elements.closeModal.addEventListener("click", hideModal);
    }

    // Modal zipcode input and submit (different from main input)
    const modalInput = document.getElementById("modalZipcodeInput");
    const modalSubmit = document.getElementById("modalSubmitZipcode");
    
    if (modalInput && modalSubmit) {
      modalSubmit.addEventListener("click", async () => {
        const zipcodeValue = modalInput.value?.trim();
        
        if (!zipcodeValue || !isValidZipcode(zipcodeValue)) {
          const modalError = document.getElementById("modalZipcodeError");
          if (modalError) {
            modalError.textContent = "Please enter a valid ZIP code.";
            modalError.classList.remove("hide");
          }
          return;
        }

        modalSubmit.disabled = true;
        modalSubmit.textContent = "Updating...";

         try {
           const { updateZipcode } = useDelivery();
           updateZipcode(zipcodeValue);
           await fetchAndDisplayDeliveryData(zipcodeValue);
          hideModal();
        } catch (error) {
          console.error("Error updating zipcode from modal:", error);
          const modalError = document.getElementById("modalZipcodeError");
          if (modalError) {
            modalError.textContent = "Unable to fetch delivery estimate. Please try again.";
            modalError.classList.remove("hide");
          }
        } finally {
          modalSubmit.disabled = false;
          modalSubmit.textContent = "Update";
        }
      });

      modalInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          modalSubmit.click();
        }
        if (event.key === "Escape") {
          hideModal();
        }
      });

      modalInput.addEventListener("input", (event) => {
        event.target.value = event.target.value.replace(/\D/g, "").slice(0, 5);
        const modalError = document.getElementById("modalZipcodeError");
        if (modalError) {
          modalError.classList.add("hide");
        }
      });
    }

    // Click outside modal to close
    if (elements.modal) {
      elements.modal.addEventListener("click", (event) => {
        if (event.target === elements.modal) {
          hideModal();
        }
      });
    }

  }

  /**
   * Initialize the component
   */
  async function init() {
    try {
      const { zipcode } = useDelivery();

      // Check if zip code exists in session storage
      const storedZip = sessionStorage.getItem("SDG-zip");
      
      if (storedZip) {
        zipcode.value = storedZip;
      }

      // Check for selected store and update pickup section
      const store = getSelectedStore();
      
      if (store) {
        updatePickupSection(store);
      }

      if (zipcode.value) {
        await fetchAndDisplayDeliveryData(zipcode.value);
      } else {
        showScenario(SCENARIOS.NO_ZIP);
      }

      setupEventListeners();
    } catch (error) {
      console.error("❌ Enhanced Delivery Estimate initialization error - hiding EDD component:", error);
      console.error("Error stack:", error.stack);
      component.classList.add(CSS_CLASSES.HIDE);
    }
  }

  // Initialize the component
  init();

  // Listen for zipcode being set by store-locator (ipstack geolocation)
  // This handles the case where the component initialized before the zip was available
  window.addEventListener("SDG-zip-updated", async (event) => {
    const { zip } = event.detail || {};
    if (zip) {
      await fetchAndDisplayDeliveryData(zip);
    }
  });

  // Listen for an art type being selected in the "What Do You Want To Frame?" dropdown
  document.addEventListener("framingOption:selected", async (event) => {
    if (productType !== "custom frame") {
      return;
    }

    const { slug } = event.detail || {};
    selectedConveyance = DIGITAL_ART_TYPE_SLUGS.includes(slug) ? "digital" : "physical";

    const { zipcode } = useDelivery();
    if (deliveryData && zipcode.value) {
      await displayCustomFrameEstimate(zipcode.value);
    }
  });

  // Listen for nearest stores being detected by store-locator
  // This handles the case where the component initialized before nearest stores were available
  window.addEventListener("nearestStoresUpdated", (event) => {
    // Only auto-select nearest store if no store is already manually selected
    const manuallySelectedStore = localStorage.getItem("selectedStore");
    if (!manuallySelectedStore) {
      const { stores } = event.detail || {};
      if (stores && stores.length > 0) {
        updatePickupSection(stores[0]);
      }
    }
  });

  // Return public methods if needed
  return {
    refresh: init,
    showScenario,
    updateZipcode: async (zipcode) => {
      try {
        const { updateZipcode } = useDelivery();
        updateZipcode(zipcode);
        await fetchAndDisplayDeliveryData(zipcode);
      } catch (error) {
        console.error("Enhanced Delivery Estimate: Update zipcode failed", error);
      }
    }
  };
}

export default enhancedDeliveryEstimate;
